import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HD_BODYGRAPH_CHANNELS } from '../hd-bodygraph-metadata';
import {
  HD_CHANNEL_TYPE_ORDER,
  HD_CREATIVE_CHANNEL_KEYS,
  HdChannelTypeKey,
  hdChannelTypeFor,
  isHdManifestingGeneratorChannel,
} from '../hd-channel-type-metadata';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';

const P = 'humanDesign.chart.channelInfo.allChannels';

interface ChannelRowVm {
  readonly key: string;
  readonly name: string;
  readonly shortDescription: string;
  readonly badges: readonly string[];
}

interface ChannelTypeGroupVm {
  readonly key: HdChannelTypeKey;
  readonly title: string;
  readonly hint: string;
  readonly channels: readonly ChannelRowVm[];
}

/**
 * "Alle kanalen"-overzicht in de doc-viewer: de 36 kanalen gegroepeerd per
 * kanaaltype volgens Channels by Type (manifested / generated / projected,
 * afgeleid uit de center-topologie — zie {@link hdChannelTypeFor}), met per
 * kanaal naam + "een design van" en waar relevant de Manifesting-Generator- of
 * Creatief-badge. Elke rij is een gewone `<a href="<key>.md">`: de
 * click-delegatie van {@link HdDocViewerComponent} vangt hem op en navigeert
 * in hetzelfde paneel naar het kanaaldocument (met terugknop via de history).
 */
@Component({
  selector: 'hd-channels-overview-info',
  templateUrl: './hd-channels-overview-info.component.html',
  styleUrl: './hd-channels-overview-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class HdChannelsOverviewInfoComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly title = computed(() => this.t('title'));
  protected readonly intro = computed(() => this.t('intro'));

  protected readonly groups = computed<readonly ChannelTypeGroupVm[]>(() => {
    const lang = this.lang();
    const badgeMg = this.t('badges.mg');
    const badgeCreative = this.t('badges.creative');
    return HD_CHANNEL_TYPE_ORDER.map(type => ({
      key: type,
      title: this.t(`groups.${type}.title`),
      hint: this.t(`groups.${type}.hint`),
      channels: HD_BODYGRAPH_CHANNELS
        .filter(channel => hdChannelTypeFor(channel) === type)
        .map<ChannelRowVm>(channel => {
          const translation = this.gateInfo.channelTranslationIn(channel.key, lang);
          return {
            key: channel.key,
            name: translation?.name ?? channel.key,
            shortDescription: translation?.shortDescription ?? '',
            badges: [
              ...(isHdManifestingGeneratorChannel(channel.key) ? [badgeMg] : []),
              ...(HD_CREATIVE_CHANNEL_KEYS.has(channel.key) ? [badgeCreative] : []),
            ],
          };
        }),
    }));
  });

  private t(key: string): string {
    return this.transloco.translate<string>(`${P}.${key}`, undefined, this.lang());
  }
}
